import React, { createContext, useContext, useState, useMemo, useEffect, useLayoutEffect } from 'react';
import { createTheme } from '@mui/material/styles';

const THEME_MODE_KEY = 'themeMode';
const ThemeContext = createContext();

export const useTheme = () => useContext(ThemeContext);

const getStoredTheme = () => {
    try {
        const storedTheme = localStorage.getItem(THEME_MODE_KEY);
        return storedTheme || 'dark';
    } catch {
        return 'dark';
    }
};

export const ThemeProvider = ({ children }) => {
    const [mode, setMode] = useState(getStoredTheme);

    // Sync theme across tabs and ensure it's loaded before render
    useLayoutEffect(() => {
        const storedTheme = getStoredTheme();
        if (storedTheme !== mode) {
            setMode(storedTheme);
        }
        document.documentElement.setAttribute('data-theme', storedTheme);
    }, []);

    // Listen for theme changes in other tabs
    useEffect(() => {
        const handleStorageChange = (e) => {
            if (e.key === THEME_MODE_KEY && e.newValue) {
                setMode(e.newValue);
                document.documentElement.setAttribute('data-theme', e.newValue);
            }
        };

        window.addEventListener('storage', handleStorageChange);
        
        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);

    // Update theme in localStorage and DOM
    useEffect(() => {
        try {
            localStorage.setItem(THEME_MODE_KEY, mode);
            document.documentElement.setAttribute('data-theme', mode);
        } catch (error) {
            console.error('Failed to save theme preference:', error);
        }
    }, [mode]);

    const theme = useMemo(() => createTheme({
        palette: {
            mode,
            ...(mode === 'dark' ? {
                background: {
                    default: '#0A1429',
                    paper: '#0F1A2E',
                    sidebar: '#0A1429',
                    table: {
                        header: '#0F1A2E',
                        row: '#0A1429',
                        hover: '#152238'
                    }
                },
                primary: {
                    main: '#60A5FA',
                    light: '#93C5FD',
                    dark: '#3B82F6'
                }
            } : {
                background: {
                    default: '#F8FAFC',
                    paper: '#FFFFFF',
                    sidebar: '#F1F5F9',
                    table: {
                        header: '#F8FAFC',
                        row: '#FFFFFF',
                        hover: '#F1F5F9'
                    }
                },
                primary: {
                    main: '#3B82F6',
                    light: '#60A5FA',
                    dark: '#2563EB'
                }
            })
        },
        components: {
            MuiDrawer: {
                styleOverrides: {
                    paper: {
                        border: 'none',
                        backgroundImage: 'none',
                        backgroundColor: mode === 'dark' ? '#0A1429' : '#F1F5F9',
                        boxShadow: mode === 'dark' 
                            ? '4px 0 6px -1px rgb(0 0 0 / 0.3)' 
                            : '4px 0 6px -1px rgb(0 0 0 / 0.1)'
                    }
                }
            },
            MuiTableContainer: {
                styleOverrides: {
                    root: {
                        backgroundColor: mode === 'dark' ? '#0F1A2E' : '#FFFFFF',
                        borderRadius: '12px',
                        boxShadow: mode === 'dark' 
                            ? '0 4px 6px -1px rgb(0 0 0 / 0.3)' 
                            : '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                    }
                }
            },
            MuiTableHead: {
                styleOverrides: {
                    root: {
                        '& .MuiTableCell-head': {
                            backgroundColor: mode === 'dark' ? '#0F1A2E' : '#F8FAFC',
                            fontWeight: 600
                        }
                    }
                }
            },
            MuiTableRow: {
                styleOverrides: {
                    root: {
                        '&:hover': {
                            backgroundColor: mode === 'dark' ? '#152238' : '#F1F5F9'
                        }
                    }
                }
            }
        }
    }), [mode]);

    const toggleTheme = () => {
        const newMode = mode === 'dark' ? 'light' : 'dark';
        setMode(newMode);
        try {
            localStorage.setItem(THEME_MODE_KEY, newMode);
            document.documentElement.setAttribute('data-theme', newMode);
            // Dispatch a custom event to notify other components
            window.dispatchEvent(new CustomEvent('themeChange', { detail: newMode }));
        } catch (error) {
            console.error('Failed to save theme preference:', error);
        }
    };

    return (
        <ThemeContext.Provider value={{ theme, mode, toggleTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};
